import { useCallback, useMemo, useState } from 'react';

import { Flag, FlagActionHistory, FlagStatus, FlagSubject } from 'types';

import { sortDescending } from '../../../helpers/sort';

type ChangesMadeOnFlag = {
  document: {
    uploaded: boolean;
    text: string;
  };
  field: {
    updated: boolean;
    text: string;
  };
  date: string | undefined;
};

const determineIfDocumentOrFieldWereUpdated = (changes: ChangesMadeOnFlag) => {
  if (changes.document.uploaded) {
    return {
      changes: changes.document.uploaded,
      text: changes.document.uploaded ? 'Document Uploaded' : 'Action Needed',
    };
  }
  return {
    changes: changes.field.updated,
    text: changes.field.updated ? 'Field Updated' : 'Action Needed',
  };
};

export const useFlagPageState = (flag: Flag | undefined) => {
  const [selectedFlagStatus, setSelectedFlagStatus] = useState(flag?.status ?? FlagStatus.open);
  const [newNote, setNewNote] = useState('');
  const [changesOnFlag, setChangesOnFlag] = useState(false);
  const [replacementFileUpload, setReplacementFileUpload] = useState<string | undefined>();
  const [epcFeedbackFileUpload, setEpcFeedbackFileUpload] = useState<string | undefined>();
  const [replacementValue, setReplacementValue] = useState<string | undefined>();
  const [flagFiles, setFlagFiles] = useState<FileList | undefined>();
  const [flagIndexKey, setFlagIndexKey] = useState(-1);

  const sortedActionHistory = useMemo(() => {
    return flag?.actionHistory ? sortDescending(flag.actionHistory, 'date') : [];
  }, [flag?.actionHistory]);

  const changesMadeOnFlag = useMemo((): ChangesMadeOnFlag => {
    return {
      document: {
        uploaded: sortedActionHistory?.some(
          (doc: FlagActionHistory) =>
            doc.flagSubject === FlagSubject.documentUpload ||
            (doc.flagSubject === FlagSubject.epcFeedback && doc.fileUpload),
        ),
        text: 'Document Uploaded',
      },
      field: {
        updated: sortedActionHistory?.some((doc: FlagActionHistory) => doc.flagSubject === FlagSubject.dataInput),
        text: 'Field Updated',
      },
      date: sortedActionHistory?.find(
        (doc: FlagActionHistory) =>
          doc.flagSubject === FlagSubject.documentUpload ||
          doc.flagSubject === FlagSubject.dataInput ||
          (doc.flagSubject === FlagSubject.epcFeedback && doc.fileUpload),
      )?.date,
    };
  }, [sortedActionHistory]);

  const badgeTextValuesAccordingToChangesType = determineIfDocumentOrFieldWereUpdated(changesMadeOnFlag);

  const saveFilesForFutureUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFlagFiles(event.target.files ?? undefined);
  }, []);

  return {
    selectedFlagStatus,
    setSelectedFlagStatus,
    newNote,
    setNewNote,
    changesOnFlag,
    setChangesOnFlag,
    replacementFileUpload,
    setReplacementFileUpload,
    epcFeedbackFileUpload,
    setEpcFeedbackFileUpload,
    replacementValue,
    setReplacementValue,
    flagFiles,
    setFlagFiles,
    flagIndexKey,
    setFlagIndexKey,
    sortedActionHistory,
    changesMadeOnFlag,
    badgeTextValuesAccordingToChangesType,
    saveFilesForFutureUpload,
  };
};
